import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  output,
} from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import type { CommentAttachment } from '@tourfold/libraries/tf-api-client-public-v2';
import { AttachmentChipComponent } from './attachment-chip.component';

/**
 * Chip-row presentation for a comment's non-inline attachments. Inline rows
 * (`is_inline=true`) are filtered out here because they're already rendered in the body via
 * the markdown `![alt](attachment:<uuid>)` token. The remaining attachments keep their server
 * `position` ordering — the row is a thin, deterministic projection.
 *
 * Component-level filtering (rather than asking each caller to filter) keeps the contract
 * "give me a comment's attachments array" instead of "give me only the chips" — same shape the
 * server emits in `CommentResponse.attachments`.
 */
@Component({
  selector: 'tf-attachments-row',
  standalone: true,
  imports: [TranslateModule, AttachmentChipComponent],
  templateUrl: './attachments-row.component.html',
  styleUrl: './attachments-row.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AttachmentsRowComponent {
  readonly attachments = input.required<readonly CommentAttachment[]>();
  readonly attachmentClick = output<CommentAttachment>();

  protected readonly chips = computed(() => this.attachments().filter((a) => !a.isInline));
}
